import { useEffect, useState } from 'react';
import {
  SOAR_RATIO,
  BREACH_RATIO,
  DISCOUNT_PRICE,
  LOWER_PRICE_RANGE,
  HIGHER_PRICE_RANGE,
  SOFTWARE_DISCOUNT,
  SAAS_DISCOUNT,
  PartNumber,
  SAAS_TIERS,
} from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import { useCurrencyWithSymbol } from 'src/common/hooks/widgets/useCurrencyWithSymbol';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} localCurrency the language code to get the currency sign for
 * @param {number} employees : Number of employees/workstations
 * @param {boolean} breach : Includes Breach
 * @returns {Array}
 */

export interface PropsFunc {
  currency: string;
  localeCode: string;
  country: string;
  employees: number;
  breach: boolean;
  onPrem: boolean;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

const partsToQuery = [
  {
    partNumber: PartNumber.software,
  },
  {
    partNumber: PartNumber.breach,
  },
  {
    partNumber: PartNumber.saas,
  },
];

interface PriceParts {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: ScaleQtyPrice[];
}

interface ScaleQtyPrice {
  qty: number;
  price: number;
}

export const usePrice = ({
  currency,
  localeCode,
  country,
  employees,
  breach,
  onPrem,
}: PropsFunc) => {
  const [totalCost, setTotalCost] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [lowerPrice, setLowerPrice] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [higherPrice, setHigherPrice] = useCurrencyWithSymbol(
    currency,
    localeCode,
    0,
  );
  const [total, setTotal] = useState(0);
  const [ruMonthlySublistPrice, setRuMonthlySublistPrice] = useState(0);
  const [saasBreachPrice, setSaasBreachPrice] = useState(0);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [saasTierPrices, setSaasTierPrices] = useState<number[]>([]);

  const calculatedSWPrice = () => {
    let price;

    if (!breach) {
      price =
        (employees *
          SOAR_RATIO *
          ruMonthlySublistPrice *
          DISCOUNT_PRICE *
          SOFTWARE_DISCOUNT) /
        100;
    } else {
      price =
        (employees *
          SOAR_RATIO *
          ruMonthlySublistPrice *
          DISCOUNT_PRICE *
          SOFTWARE_DISCOUNT) /
          100 +
        (employees *
          BREACH_RATIO *
          ruMonthlySublistPrice *
          DISCOUNT_PRICE *
          SOFTWARE_DISCOUNT) /
          100;
    }
    return price;
  };

  const calculatePricing = (qty: number, price: number) => {
    return qty * price;
  };

  /**
   * Calculate the final price according to quantity and price per tier
   *
   * @param {number} employees : no of employees
   * @param {Price[]} PRICES : Array of prices per tier
   * @returns {number}
   */
  const calculateSaasTierPrice = (
    employees: number,
    PRICES: number[],
  ): number => {
    const [SAAS_TIER_ONE_PRICE, SAAS_TIER_TWO_PRICE, SAAS_TIER_THREE_PRICE] =
      PRICES;
    let price;
    switch (true) {
      case employees >= SAAS_TIERS.TIER_1 && employees < SAAS_TIERS.TIER_2:
        price = calculatePricing(employees, SAAS_TIER_ONE_PRICE);
        break;
      case employees >= SAAS_TIERS.TIER_2 && employees < SAAS_TIERS.TIER_3:
        price = calculatePricing(employees, SAAS_TIER_TWO_PRICE);
        break;
      case employees >= SAAS_TIERS.TIER_3 && employees < SAAS_TIERS.TIER_4:
        price = calculatePricing(employees, SAAS_TIER_THREE_PRICE);
        break;
      default:
        price = 0;
        break;
    }
    return price;
  };

  const calculateSaaSPrice = () => {
    let price;
    const saasTierPrice =
      calculateSaasTierPrice(employees, saasTierPrices) * SAAS_DISCOUNT;
    if (!breach) {
      price = saasTierPrice;
    } else {
      price = saasTierPrice + employees * saasBreachPrice * SAAS_DISCOUNT;
    }
    return price;
  };

  const isUndefinedForNumber = (value: number | undefined) => {
    return value ? value : 0;
  };

  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      const priceArray = products.map((item) => ({
        [item.partNumber]: item.scaleQtyPrice[0].price,
      }));
      const priceObj = Object.assign({}, ...priceArray);
      setRuMonthlySublistPrice(priceObj.D0AE7ZX);
      setSaasBreachPrice(priceObj.D0DSDZX);

      // new tier prices for saas
      const SAAS_PRICE = products.find(
        (i: PriceParts) => i.partNumber == PartNumber.saas,
      );

      const SAAS_PRICE_ONE = SAAS_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == SAAS_TIERS.TIER_1,
      )?.price;
      const SAAS_PRICE_TWO = SAAS_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == SAAS_TIERS.TIER_2,
      )?.price;
      const SAAS_PRICE_THREE = SAAS_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == SAAS_TIERS.TIER_3,
      )?.price;
      const SAAS_PRICE_LAST = SAAS_PRICE?.scaleQtyPrice.find(
        (i: ScaleQtyPrice) => i.qty == SAAS_TIERS.TIER_4,
      )?.price;

      const SAAS_TIER_PRICES: number[] = [
        isUndefinedForNumber(SAAS_PRICE_ONE),
        isUndefinedForNumber(SAAS_PRICE_TWO),
        isUndefinedForNumber(SAAS_PRICE_THREE),
        isUndefinedForNumber(SAAS_PRICE_LAST),
      ];
      setSaasTierPrices(SAAS_TIER_PRICES);
    }
  };

  const fetchPrices = () => {
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  const price = onPrem
    ? calculatedSWPrice()
    : saasTierPrices.length > 0
    ? calculateSaaSPrice()
    : 0;

  useEffect(() => {
    if (currency && country && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [localeCode, currency, country]);

  useEffect(() => {
    setLowerPrice(Math.round(price * LOWER_PRICE_RANGE));
    setHigherPrice(Math.round(price * HIGHER_PRICE_RANGE));
    setTotalCost(Math.round(price));
    setTotal(Math.round(price));
  }, [employees, breach, ruMonthlySublistPrice, onPrem, price, saasTierPrices]);

  return [totalCost, lowerPrice, higherPrice, total, fetchingError];
};
